import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';

import { IEvl0101MState, IEvaluationParamsState, IInquiryCndsState, ICurrentUserState,  Employee,  PageType, EvaluationCycle, EvaluationType } from '../../models/evl/evl0101m_model';

const initialState: IEvl0101MState = {
  currentPage        : 'dashboard',
  evaluationParams   : {},
  employees          : [],
  currentUser        : {
    id  : '',
    name: '',
    role: ''
  },
  inquiryCnds        : {},
  evaluationCycles   : [],
  evaluationDeadlines: {
    "work-commitment"   : "",
    "self-evaluation"   : "",
    "first-evaluation"  : "",
    "second-evaluation" : "",
    "project-evaluation": ""
  },
  loading           : false,
  error             : null,
  activeTab         : 'hr',
  selectedProjectId : null,
  selectedEmployeeId: null
};

const evl0101mSlice = createSlice({
  name: 'evl0101m',
  initialState,
  reducers: {
    // 상태 초기화
    resetState: () => initialState,

    // 대시보드로 이동
    goToDashboard(state) {
      state.currentPage      = 'dashboard';
      state.evaluationParams = {};
    },

    // 평가 페이지로 이동
    goToEvaluation(
      state,
      action: PayloadAction<IEvaluationParamsState>
    ) {
      state.currentPage      = 'evaluation';
      state.evaluationParams = action.payload;
    },

    // 현재 페이지 설정
    setCurrentPage(
      state,
      action: PayloadAction<PageType>
    ) {
      state.currentPage = action.payload;
    },

    // 평가 파라미터 설정
    setEvaluationParams(
      state,
      action: PayloadAction<IEvaluationParamsState>
    ) {
      state.evaluationParams = {
        ...state.evaluationParams,
        ...action.payload
      };
    },

    // 조회 조건 설정
    setInquiryCnds(
      state,
      action: PayloadAction<IInquiryCndsState>
    ) {
      state.inquiryCnds = {
        ...state.inquiryCnds,
        ...action.payload
      };
    },

    // 직원 목록 설정
    setEmployees(
      state,
      action: PayloadAction<Employee[]>
    ) {
      state.employees = action.payload;
      state.loading = false;
    },

    // 현재 사용자 설정
    setCurrentUser(
      state,
      action: PayloadAction<ICurrentUserState>
    ) {
      state.currentUser = action.payload;
    },

    // 평가 주기 설정
    setEvaluationCycles(
      state,
      action: PayloadAction<EvaluationCycle[]>
    ) {
      state.evaluationCycles = action.payload;
    },

    // 평가 마감일 설정
    setEvaluationDeadlines(
      state,
      action: PayloadAction<Record<string, string>>
    ) {
      state.evaluationDeadlines = action.payload as any;
    },

    // 로딩 상태 설정
    setLoading(
      state,
      action: PayloadAction<boolean>
    ) {
      state.loading = action.payload;
    },

    // 에러 설정
    setError(
      state,
      action: PayloadAction<string | null>
    ) {
      state.error = action.payload;
      state.loading = false;
    },

    // 특정 직원의 평가 상태 업데이트
    updateEmployeeEvaluation(
      state,
      action: PayloadAction<{
        employeeId    : string;
        evaluationType: string;
        isCompleted   : string;
      }>
    ) {
      const employee = state.employees.find(emp => emp.evlId === action.payload.employeeId);
      if (employee) {
        const { evaluationType, isCompleted } = action.payload;
        if (evaluationType in employee.evaluations) {
          (employee.evaluations as any)[evaluationType] = isCompleted;
        }
      }
    },

    // 특정 직원의 프로젝트 평가 상태 업데이트
    updateProjectEvaluation(
      state,
      action: PayloadAction<{
        employeeId : string;
        projectId  : string;
        isCompleted: string;
      }>
    ) {
      const employee = state.employees.find(emp => emp.evlId === action.payload.employeeId);
      if (employee) {
        const project = employee.evaluations.projectEvaluations.find(
          p => p.evlId === action.payload.projectId
        );
        if (project) {
          project.isCompleted = action.payload.isCompleted;
        }
      }
    },

    // 에러 초기화
    clearError(state) {
      state.error = null;
    },

    // 활성 탭 변경
    setActiveTab(
      state,
      action: PayloadAction<"hr" | "project">
    ) {
      state.activeTab = action.payload;
    },

    // 프로젝트 선택
    selectProject(
      state,
      action: PayloadAction<{ projectId: string; employeeId: string }>
    ) {
      state.selectedProjectId  = action.payload.projectId;
      state.selectedEmployeeId = action.payload.employeeId;
    },

    // 프로젝트 선택 해제
    clearSelection(state) {
      state.selectedProjectId  = null;
      state.selectedEmployeeId = null;
    },

    // 전체 초기 데이터 설정 (백엔드 연동 시 사용)
    initializeData(
      state,
      action: PayloadAction<{
        employees          : Employee[];
        currentUser        : ICurrentUserState;
        evaluationCycles   : EvaluationCycle[];
        evaluationDeadlines: Record<EvaluationType, string>;
      }>
    ) {
      state.employees           = action.payload.employees;
      state.currentUser         = action.payload.currentUser;
      state.evaluationCycles    = action.payload.evaluationCycles;
      state.evaluationDeadlines = action.payload.evaluationDeadlines;
      state.loading             = false;
      state.error               = null;
    }
  }
});

export default evl0101mSlice.reducer;
export const Evl0101mAction  = evl0101mSlice.actions;
export const Evl0101mReducer = evl0101mSlice.reducer;